import clsx from 'clsx';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePDPData } from '~/features/product-details/context';
import SadFaceIcon from '~/icons/sad-face.svg';
import TruckIcon from '~/icons/truck.svg';
import { SessionStorage } from '~/services/storage/session-storage';
import { Typography } from '~/shared/components';
import { useFrame } from '~/shared/utils';
import { computeDeliveryState, DeliveryStateComputed } from '~/shared/utils/sameDayDelivery';
import { useTranslation } from '~/shared/utils/translation';
import { TranslateFn } from '~/shared/utils/translation/hooks/useTranslation';
import { includedZipSet, ZIP_REGEX } from './constants';
import { ProductZipCheck } from './ProductZipCheck';
import styles from './ProductExpressDelivery.module.scss';

const ZIP_STORAGE_KEY = 'zipPreference';
const storage = new SessionStorage();

const readStoredZip = () => storage.get(ZIP_STORAGE_KEY) ?? '';
const writeStoredZip = (zip: string) => {
    if (zip) storage.set(ZIP_STORAGE_KEY, zip);
    else storage.delete(ZIP_STORAGE_KEY);
};

const isZipDeliverable = (zip: string) => ZIP_REGEX.test(zip) && includedZipSet.has(zip);

interface Message {
    title?: string;
    text?: string;
    status?: 'success' | 'error';
    icon?: React.FC<React.SVGProps<SVGSVGElement>>;
    iconPosition?: 'left' | 'right';
}

const getExpressDeliveryMessage = (
    state: DeliveryStateComputed,
    translate: TranslateFn,
): Message => {
    const { deliveryState: internalDeliveryState, timeRemainingText, cutoffText } = state;

    let message: Message;

    switch (internalDeliveryState) {
        case 'today':
            message = {
                title: translate('pdp.sameDayDelivery.orderTodayTitle'),
                text: translate('pdp.sameDayDelivery.orderTodayText', {
                    timeRemainingText: timeRemainingText ?? '',
                }),
            };
            break;

        case 'tomorrow':
            message = {
                title: translate('pdp.sameDayDelivery.orderTomorrowTitle'),
                text: translate('pdp.sameDayDelivery.orderTomorrowText'),
            };
            break;

        case 'sunday':
            message = {
                title: translate('pdp.sameDayDelivery.orderSundayTitle'),
                text: translate('pdp.sameDayDelivery.orderSundayText'),
            };
            break;

        case 'christmasEve':
            message = {
                text: `Bestil inden ${timeRemainingText || cutoffText} og få leveret i aften`,
            };
            break;

        case 'christmasEveTomorrow':
            message = {
                title: 'Du kan få den leveret allerede den 23. december',
                text: `Bestil inden ${timeRemainingText || cutoffText} og få leveret om aftenen d. 23. dec.`,
            };
    }

    return {
        ...message,
        status: 'success',
    };
};

type Props = React.HTMLAttributes<HTMLDivElement>;

export const ProductExpressDelivery: React.FC<Props> = ({ className, ...rest }) => {
    const { translate } = useTranslation();
    const { data: frame } = useFrame();
    const { product } = usePDPData();

    const [delivery, setDelivery] = useState(computeDeliveryState);
    const [zip, setZip] = useState('');

    const isEnabled = frame.productSettings?.shouldShowBurdBanner !== false;

    const isZipFormatValid = (text: string) => ZIP_REGEX.test(text);

    // Read after mount so the server and the first client render agree on an empty field.
    useEffect(() => setZip(readStoredZip()), []);

    // Only a postcode we can actually deliver to is worth remembering.
    const onZipChange = useCallback((value: string) => {
        setZip(value);

        if (isZipFormatValid(value)) {
            const zipToSave = isZipDeliverable(value) ? value : '';

            writeStoredZip(zipToSave);
        }
    }, []);

    // The cutoff passes while the page is open, so the wording is re-checked as the clock moves.
    useEffect(() => {
        const interval = setInterval(() => setDelivery(computeDeliveryState()), 60 * 1000); // 1 minute

        return () => clearInterval(interval);
    }, []);

    const result = useMemo<Message | undefined>(() => {
        if (!zip) return undefined;

        if (!isZipFormatValid(zip)) {
            return {
                text: translate('pdp.sameDayDelivery.zipCodeErrorText'),
                status: 'error',
            };
        }

        if (!isZipDeliverable(zip)) {
            return {
                title: translate('pdp.sameDayDelivery.zipCodeUnsupportedTitle'),
                text: translate('pdp.sameDayDelivery.zipCodeUnsupportedText'),
                icon: SadFaceIcon,
                iconPosition: 'right',
            };
        }

        return getExpressDeliveryMessage(delivery, translate);
    }, [delivery, zip]);

    if (!isEnabled || !product.c_burdFlag) return null;

    const {
        icon: Icon = TruckIcon,
        iconPosition = 'left',
        title = translate('pdp.sameDayDelivery.zipCodeTitle'),
        text = '',
        status,
    } = result || {};

    const stateClass = status ? styles[status] : undefined;

    return (
        <div className={clsx(styles.productExpressDelivery, stateClass, className)} {...rest}>
            <div className={styles.productExpressDeliveryHeader}>
                <Icon className={clsx(styles.icon, styles[`position-${iconPosition}`])} />

                <Typography variant="paragraph-S" weight="medium">
                    {title}
                </Typography>
            </div>

            <ProductZipCheck
                className={styles.productExpressDeliveryInput}
                value={zip}
                isInvalid={status === 'error'}
                onChange={onZipChange}
            />

            <Typography
                className={styles.productExpressDeliveryResult}
                variant="paragraph-S"
                as="p"
            >
                {text}
            </Typography>
        </div>
    );
};
